import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { type ProspectStatusKey, type TokenKey } from '@/theme/tokens';
import { statusEntry, toneStyle } from '@/components/vf/statusMap';

// Mapping dynamique Tailwind classes — on utilise des lookups explicites
// pour que le JIT de Tailwind détecte les classes utilisées.
const BG: Partial<Record<TokenKey, string>> = {
  bb: 'bg-bb', ob: 'bg-ob', gb: 'bg-gb', pb: 'bg-pb',
  rb: 'bg-rb', ab: 'bg-ab', cyb: 'bg-cyb', pkb: 'bg-pkb', ylb: 'bg-ylb',
};
const TX: Partial<Record<TokenKey, string>> = {
  bl: 'text-bl', or: 'text-or', gn: 'text-gn', pr: 'text-pr',
  rd: 'text-rd', ac: 'text-ac', cy: 'text-cy', pk: 'text-pk', yl: 'text-yl',
};

interface StatusBadgeProps {
  status: ProspectStatusKey;
  className?: string;
}

export function StatusBadge({ status, className = '' }: StatusBadgeProps) {
  const { t } = useTranslation('dialer');
  // D9 : couleur et icône de la carte unique (components/vf/statusMap). Un
  // statut inconnu prend l'entrée neutre « nouveau » plutôt que rien.
  const entry = statusEntry(status);
  const style = toneStyle(entry.tone);
  const Icon = entry.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${className}`}
      style={{ background: style.bg, color: style.fg, border: `1px solid ${style.bd}` }}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {outcomeLabel(status, t)}
    </span>
  );
}

interface BadgeProps {
  bgToken?: TokenKey;
  txToken?: TokenKey;
  children?: ReactNode;
  className?: string;
  icon?: ReactNode;
}

// Badge générique piloté par tokens (bgToken = clé comme "gb", txToken = "gn").
export function Badge({ bgToken, txToken, children, className = '', icon }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${(bgToken && BG[bgToken]) || ''} ${(txToken && TX[txToken]) || 'text-tx'} ${className}`}
    >
      {icon}
      {children}
    </span>
  );
}

export const BADGE_BG = BG;
export const BADGE_TX = TX;
